import Option, { option } from '../util/Option'
import { AppState, Dispatch, ExtraArgs, GetState } from '../types'
import { combineReducers } from 'redux'
import { actions as lifecycleActions } from './lifecycle'
import { Entitlements, Organization as CSOrganization, Profile as CSProfile } from '../remote-api/CoreServicesRemoteApi'
import { buildAuthorizeUrl } from '@mulesoft/monitoring-center-components'
import { getOrigin } from '@mulesoft/monitoring-center-components/lib/services/auth/WindowHelper'

export type Profile = {
  firstName: string,
  lastName: string,
  username: string,
  authorization: string,
  ownerId: string,
  organizationId: string,
  organizationDomain: string,
  organizationPreferences: { [key: string]: { defaultEnvironment: string } },
  organization: CSOrganization
}

export type AuthState = {
  profile: Profile,
  roles: RoleTypes[]
  requiresProfile: boolean,
  isProfileLoading: boolean,
  isCheckingProfile: boolean,
  isProfileLoaded: boolean,
  platformToken?: string
}

export enum ProfileValues {
  PROFILE_LOAD_STARTED = 'MONITORING/AUTH/PROFILE_LOAD_STARTED',
  PROFILE_LOAD_FAILED = 'MONITORING/AUTH/PROFILE_LOAD_FAILED',
  PROFILE_IS_NOT_REQUIRED = 'MONITORING/AUTH/PROFILE_IS_NOT_REQUIRED',
  PROFILE_NEEDS_RELOAD = 'MONITORING/AUTH/PROFILE_NEEDS_RELOAD',
  SIGN_OUT_REQUEST = 'MONITORING/AUTH/SIGN_OUT',
  PROFILE_CHECK_STARTED = 'MONITORING/AUTH/PROFILE_CHECK_STARTED',
  PROFILE_CHECK_ENDED = 'MONITORING/AUTH/PROFILE_CHECK_ENDED'
}

export enum ProfileLoaderValues {
  PROFILE_LOADED = 'MONITORING/AUTH/PROFILE_LOADED',
  SET_TOKEN = 'MONITORING/AUTH/SET_TOKEN'
}

export enum PlatformTokenActionTypes {
  ChangePlatformToken = 'MONITORING/AUTH/CHANGE_PLATFORM_TOKEN'
}

export enum RoleValues {
  ROLE_LOAD_STARTED = 'MONITORING/AUTH/ROLE_LOAD_STARTED',
  ROLE_LOADED = 'MONITORING/AUTH/ROLE_LOADED',
}

export enum RoleTypes {
  ADMIN = 'admin',
  VIEWER = 'viewer',
}

export type AuthAction =
  | { type: ProfileValues }
  | { type: ProfileLoaderValues, payload: any }
  | { type: PlatformTokenActionTypes, payload: string }
  | { type: RoleValues, payload: RoleTypes[] }

export const changePlatformToken = (token: string) => ({type: PlatformTokenActionTypes.ChangePlatformToken, payload: token})

export const profileNeedsReLoad = () => ({type: ProfileValues.PROFILE_NEEDS_RELOAD})

export const getOrganizationId = (profile: CSProfile) => {
  if (profile.properties.cs_auth && profile.properties.cs_auth.activeOrganizationId) {
    return profile.properties.cs_auth.activeOrganizationId
  }

  return profile.organization.id
}

export const loadProfile = (token?: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<boolean> => {
  dispatch({type: ProfileValues.PROFILE_LOAD_STARTED})

  return api
    .cs(getState)
    .auth(token)
    .then((json: CSProfile) => {
      const payload = {
        ...json,
        firstName: json.firstName,
        lastName: json.lastName,
        username: json.username,
        authorization: json.access_token,
        ownerId: json.id,
        organizationId: getOrganizationId(json),
        organizationDomain: json.organization.domain,
        organizationPreferences: json.organizationPreferences,
        organization: json.organization
      }

      if (window.analytics) {
        window.analytics.identify(json.id, {masterOrgId: json.organizationId})
      }

      dispatch({type: ProfileLoaderValues.PROFILE_LOADED, payload})

      return true
    })
    .catch((err) => {
      console.error(err)
      dispatch({type: ProfileValues.PROFILE_LOAD_FAILED})

      return false
    })
}

export const checkProfile = (token?: string) => (dispatch: Dispatch): Promise<boolean> => {
  dispatch({type: ProfileValues.PROFILE_CHECK_STARTED})

  return dispatch(loadProfileAndRoles(token))
    .then(result => {
      dispatch({type: ProfileValues.PROFILE_CHECK_ENDED})
      if (result) dispatch(lifecycleActions.update())
      return result
    })
}

export const loadProfileAndUpdate = (token?: string) => (dispatch: Dispatch): Promise<void> => {
  return dispatch(loadProfileAndRoles(token))
    .then(result => {
      if (result) {
        dispatch(lifecycleActions.update())
      }
    })
}

export const signOut = () => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): void => {
  dispatch({type: ProfileValues.SIGN_OUT_REQUEST})
  sessionStorage.clear()
  api.cs(getState).logOut()
}

export const getOAuthInfo = () => {
  const callbackEnabled = window.mc.featureFlags.oauth.callback.enabled === 'true'
  const isDev = process.env.NODE_ENV === 'development'
  const state = JSON.stringify({redirectUri: window.location.href})
  const origin = getOrigin()
  const options = {
    clientId: 'monitoring_center_ui',
    responseType: callbackEnabled ? 'code' : 'token',
    state,
    redirectUri: callbackEnabled ? `${origin}/monitoring/callback` : undefined
  }
  const loginUrl = `${buildAuthorizeUrl(options)}`

  return {isDev, loginUrl}
}

export const goToSignIn = () => {
  const {loginUrl} = getOAuthInfo()
  window.location.href = loginUrl
}

export const loadRoles = ()  => async (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch({type: RoleValues.ROLE_LOAD_STARTED})
  try {
    const organizationId = getState().monitoring.auth.profile.organizationId
    const roles = await api.cs(getState).fetchMonitoringRoles(organizationId)
    const payload = roles.map(role => role.split('/').pop())
    dispatch({type: RoleValues.ROLE_LOADED, payload})
    return true
  } catch {
    return false
  }
}

export const loadProfileAndRoles = (token?: string) => async (dispatch: Dispatch) => {
  try {
    const profileResult = await dispatch(loadProfile(token))
    const rolesResult = await dispatch(loadRoles())
    return profileResult && rolesResult
  } catch {
    return false
  }
}

/**
 *  Reducer
 */

const platformTokenReducer = (state: AuthState['platformToken'] = '', action: AuthAction): AuthState['platformToken'] => {
  switch (action.type) {
    case PlatformTokenActionTypes.ChangePlatformToken:
      return action.payload

    default:
      return state
  }
}

const rolesReducer = (state: AuthState['roles'] = [], action: AuthAction): AuthState['roles']  => {
  switch (action.type) {
    case RoleValues.ROLE_LOADED:
      return action.payload
    default:
      return state
  }
}

const profileReducer = (state: Profile | null = null, action: AuthAction): Profile | null => {
  switch (action.type) {
    case ProfileValues.PROFILE_LOAD_STARTED:
      return null
    case ProfileValues.PROFILE_LOAD_FAILED:
      return null
    case ProfileLoaderValues.PROFILE_LOADED:
      return action.payload
    case ProfileValues.SIGN_OUT_REQUEST:
      return null

    case ProfileLoaderValues.SET_TOKEN:
      return {
        ...state as Profile,
        authorization: action.payload
      }
    default:
      return state
  }
}

const isCheckingProfileReducer = (state: boolean = false, action: AuthAction): boolean => {
  switch (action.type) {
    case ProfileValues.PROFILE_CHECK_STARTED:
      return true
    case ProfileValues.PROFILE_CHECK_ENDED:
      return false
    default:
      return state
  }
}

const isProfileLoadingReducer = (state: boolean = false, action: AuthAction): boolean => {
  switch (action.type) {
    case ProfileValues.PROFILE_LOAD_STARTED:
      return true
    case ProfileValues.PROFILE_LOAD_FAILED:
    case ProfileLoaderValues.PROFILE_LOADED:
      return false
    default:
      return state
  }
}

const isProfileLoadedReducer = (state: boolean = false, action: AuthAction): boolean => {
  switch (action.type) {
    case ProfileValues.PROFILE_LOAD_STARTED:
    case ProfileValues.PROFILE_LOAD_FAILED:
    case ProfileValues.PROFILE_NEEDS_RELOAD:
    case ProfileValues.SIGN_OUT_REQUEST:
      return false
    case ProfileLoaderValues.PROFILE_LOADED:
      return true
    default:
      return state
  }
}

const requiresProfileReducer = (state: boolean = false, action: AuthAction): boolean => {
  switch (action.type) {
    case ProfileValues.PROFILE_IS_NOT_REQUIRED:
      return false
    case ProfileValues.PROFILE_LOAD_STARTED:
    case ProfileValues.PROFILE_LOAD_FAILED:
    case ProfileValues.PROFILE_NEEDS_RELOAD:
    case ProfileLoaderValues.PROFILE_LOADED:
      return true
    default:
      return state
  }
}

export const reducer: (state: AuthState, action: AuthAction) => AuthState = combineReducers({
  profile: profileReducer,
  roles: rolesReducer,
  isProfileLoading: isProfileLoadingReducer,
  isProfileLoaded: isProfileLoadedReducer,
  isCheckingProfile: isCheckingProfileReducer,
  requiresProfile: requiresProfileReducer,
  platformToken: platformTokenReducer
})

/**
 *  Selectors
 */
export const selectors = {
  getAll: (rootState: AppState): AuthState => rootState.monitoring.auth,
  requiresProfile: (rootState: AppState): boolean => selectors.getAll(rootState).requiresProfile,
  isCheckingProfile: (rootState: AppState): boolean => selectors.getAll(rootState).isCheckingProfile,
  isProfileLoading: (rootState: AppState): boolean => selectors.getAll(rootState).isProfileLoading,
  isProfileLoaded: (rootState: AppState): boolean => selectors.getAll(rootState).isProfileLoaded,
  getProfile: (rootState: AppState): Option<Profile> => option(selectors.getAll(rootState).profile),
  getRoles: (rootState: AppState): Option<RoleTypes[]> => option(selectors.getAll(rootState).roles),
  getEntitlements: (rootState: AppState): Option<Entitlements> =>
    selectors.getProfile(rootState).map(p => p.organization.entitlements),
  getApiMonitoringEntitlementSchedules: (rootState: AppState): number =>
    selectors.getEntitlements(rootState).map(e => e.apiMonitoring.schedules).getOrElse(0),
  getMonitoringCenterEntitlementSKU: (rootState: AppState): number =>
    selectors.getEntitlements(rootState).map(e => e.monitoringCenter.productSKU).getOrElse(-1),
  isMonitoringCenterPremium: (rootState: AppState): boolean =>
    selectors.getMonitoringCenterEntitlementSKU(rootState) > 0 && selectors.getMonitoringCenterEntitlementSKU(rootState) !== 3,
  getAuthorization: (rootState: AppState): string =>
    selectors.getProfile(rootState).map(p => p.authorization).getOrElse(''),
  getOrganizationId: (rootState: AppState): string =>
    selectors.getProfile(rootState).map(p => p.organizationId).getOrElse(''),
  getMasterOrganizationId: (rootState: AppState): string =>
    selectors.getProfile(rootState).map(p => p.organization.id).getOrElse(''),
  getMasterOrganizationName: (rootState: AppState): string =>
    selectors.getProfile(rootState).map(p => p.organization.name).getOrElse(''),
  getOrganizationDomain: (rootState: AppState): string =>
    selectors.getProfile(rootState).map(p => p.organizationDomain).getOrElse(''),
  getPreferredEnvironment: (rootState: AppState, orgId: string): string =>
    selectors.getProfile(rootState).map(p => {
      const pref = p.organizationPreferences[orgId]
      return pref ? pref.defaultEnvironment : ''
    }).getOrElse(''),
  getPlatformToken: (rootState: AppState): string => option(selectors.getAll(rootState).platformToken).getOrElse(''),
  hasEditRole: (rootState: AppState): boolean =>
    selectors.getRoles(rootState).getOrElse([]).indexOf(RoleTypes.ADMIN) > -1,
  hasViewRole: (rootState: AppState): boolean =>
    selectors.getRoles(rootState).getOrElse([]).indexOf(RoleTypes.VIEWER) > -1
}

export const getPlatformTokenOrFail = (state: AppState): string => {
  const result = selectors.getPlatformToken(state)
  if (!result) throw new Error('Missing API Manager token')
  return result
}
